<template>
  <VisitorWrapper>
    <CContainer v-if="!isGeneralSettingsLoading">
      <!-- SERVER ERROR -->
      <VisitorErrorHandler
        v-if="generalSettingsServerError != null"
        :error="generalSettingsServerError"
        @done="generalSettingsServerError = null"
      />
      <!-- SERVER ERROR -->

      <!-- GENERAL SETTING LIST -->
      <CRow
        v-for="setting in settingList"
        class="justify-content-center mt-4 bg-white rounded-30 p-4 d-flex flex-column gap-2"
        :class="{
          'visitor-loading': isLoading
        }"
        :key="setting.settingsKey"
      >
        <CCol md="12 text-start">
          <!-- SETTINGS CARD -->
          <VisitorSettingsControl
            :settings-key="setting.settingsKey"
            :heading="setting.heading"
            :description="setting.description"
            :show-description="setting.showDescription"
            :has-learn-more="setting.hasLearnMore"
            :learn-more="setting.learnMore"
            :has-edit="setting.hasEdit"
            :show-edit="setting.showEdit"
            :has-switch="setting.hasSwitch"
            :show-switch="setting.showSwitch"
            :options="setting.options"
            :edit="setting.edit"
            :toggle="setting.toggle"
            :error="
              setting.settingsKey === activeSettingKey ? serverError : null
            "
          >
            <!-- SETTING CARD CONTROL - RIGHT SECTION -->
            <template v-if="setting.hasControl" #control>
              <component
                v-if="setting.settingsKey === 'school-hours'"
                :is="VisitorSchoolHours"
                :settings-key="setting.settingsKey"
                :heading="setting.heading"
                :description="setting.description"
                :has-learn-more="setting.hasLearnMore"
                :learn-more="setting.learnMore"
                :has-edit="setting.hasEdit"
                :show-edit="setting.showEdit"
                :has-switch="setting.hasSwitch"
                :show-switch="setting.showSwitch"
                :options="setting.options"
                :edit="setting.edit"
                :toggle="setting.toggle"
              />
            </template>
            <!-- SETTING CARD CONTROL - RIGHT SECTION -->
          </VisitorSettingsControl>
          <!-- SETTINGS CARD -->
        </CCol>
      </CRow>
      <!-- GENERAL SETTING LIST -->
    </CContainer>
    <CContainer v-else>
      <CRow
        class="d-flex justify-content-center align-content-center mt-4 bg-white rounded-30 p-4 gap-2"
      >
        <CSpinner color="primary" />
      </CRow>
    </CContainer>
  </VisitorWrapper>
</template>
<script>
import VisitorErrorHandler from "@/v3components/VisitorErrorHandler.vue"
import VisitorWrapper from "@/v3components/VisitorWrapper.vue"
import VisitorSettingsControl from "@/v3components/Forms/Visitor/VisitorSettingsControl.vue"
import visitorConstants from "@/constants/visitorConstants"
import { computed, defineAsyncComponent, onMounted, ref } from "vue"
import { useStore } from "vuex"

const VisitorSchoolHours = defineAsyncComponent(
  () =>
    import(
      "@/v3components/Forms/Visitor/GeneralSettings/VisitorSchoolHours.vue"
    )
)

export default {
  name: "VisitorGeneralSettings",
  components: {
    VisitorSettingsControl,
    VisitorWrapper,
    VisitorErrorHandler
  },
  setup() {
    const store = useStore()

    const defaultGeneralSettingValues = {
      startTime: visitorConstants.VISITOR_DEFAULT_GENERAL_SETTINGS.START_TIME,
      endTime: visitorConstants.VISITOR_DEFAULT_GENERAL_SETTINGS.END_TIME,
      timeZone: visitorConstants.VISITOR_DEFAULT_GENERAL_SETTINGS.TIME_ZONE,
      automaticCheckout:
        visitorConstants.VISITOR_DEFAULT_GENERAL_SETTINGS.AUTOMATIC_CHECKOUT
    }

    const isGeneralSettingsLoading = ref(true)
    const isLoading = ref(false)
    const generalSettingsServerError = ref(null)
    const activeSettingKey = ref(null)
    const serverError = ref(null)

    const school = computed(() => store.getters["visitorSettings/school"])

    const visitorGeneralSettings = computed(() => {
      return school?.value?.generalSettings || []
    })

    /**
     * List of settings to be displayed on general settings page
     */
    const settingList = computed(() => [
      {
        settingsKey: "school-hours",
        heading: "School hours",
        showDescription: false,
        description: "Set default school hours",
        hasLearnMore: true,
        learnMore: "visitor_general_settings_school_hours",
        hasEdit: false,
        showEdit: false,
        hasSwitch: false,
        showSwitch: false,
        hasControl: true,
        options: {
          enabled: false,
          restricted: false
        },
        edit: async (
          schoolStartTimeValue,
          schoolEndTimeValue,
          schoolTimeZoneValue
        ) => {
          activeSettingKey.value = "school-hours"
          try {
            serverError.value = null
            isLoading.value = true
            await editSchoolHours(
              schoolStartTimeValue,
              schoolEndTimeValue,
              schoolTimeZoneValue
            )
          } catch (error) {
            activeSettingKey.value = "school-hours"
            serverError.value = error
          } finally {
            isLoading.value = false
          }
        },
        toggle: () => ({})
      },
      {
        settingsKey: "automatic-checkout",
        heading: "Automatic checkout",
        showDescription: false,
        description:
          "Enable or disable automatic checkout. If enabled, visitors will automatically be checked out at the end of the each school day",
        hasLearnMore: true,
        learnMore: "visitor_general_settings_automatic_checkout",
        hasEdit: false,
        showEdit: false,
        hasSwitch: true,
        showSwitch: true,
        hasControl: false,
        options: {
          enabled: isGeneralSettingActive(
            visitorConstants.SETTINGS.AUTOMATIC_CHECKOUT
          ),
          restricted: false
        },
        edit: () => ({}),
        toggle: async () => {
          serverError.value = null
          activeSettingKey.value = "automatic-checkout"
          try {
            isLoading.value = true
            await toggleAutomaticCheckout(
              !isGeneralSettingActive(
                visitorConstants.SETTINGS.AUTOMATIC_CHECKOUT
              )
                ? "true"
                : "false"
            )
          } catch (error) {
            activeSettingKey.value = "automatic-checkout"
            serverError.value = error
          } finally {
            isLoading.value = false
          }
        }
      }
    ])

    const getGeneralSetting = (key) => {
      return (
        visitorGeneralSettings.value?.find(
          (setting) => setting?.settings_id === key
        ) || null
      )
    }

    const isGeneralSettingActive = (key) => {
      return getGeneralSetting(key)?.value === "true"
    }

    const toggleAutomaticCheckout = async (value) => {
      const data = [
        {
          settings_id: visitorConstants.SETTINGS.AUTOMATIC_CHECKOUT,
          value: value
        },
        {
          settings_id: visitorConstants.SETTINGS.SCHOOL_START_TIME,
          value:
            getGeneralSetting(visitorConstants.SETTINGS.SCHOOL_START_TIME)
              ?.value || defaultGeneralSettingValues.startTime
        },
        {
          settings_id: visitorConstants.SETTINGS.SCHOOL_END_TIME,
          value:
            getGeneralSetting(visitorConstants.SETTINGS.SCHOOL_END_TIME)
              ?.value || defaultGeneralSettingValues.endTime
        },
        {
          settings_id: visitorConstants.SETTINGS.SCHOOL_TIMEZONE,
          value:
            getGeneralSetting(visitorConstants.SETTINGS.SCHOOL_TIMEZONE)
              ?.value || defaultGeneralSettingValues.timeZone
        }
      ]
      await store.dispatch("visitorSettings/updateSchoolGeneralSettings", {
        general_settings: data
      })
      await store.dispatch("visitorSettings/getSchoolGeneralSettings")
    }

    const editSchoolHours = async (
      schoolStartTimeValue,
      schoolEndTimeValue,
      schoolTimeZoneValue
    ) => {
      const data = [
        {
          settings_id: visitorConstants.SETTINGS.SCHOOL_START_TIME,
          value: schoolStartTimeValue
        },
        {
          settings_id: visitorConstants.SETTINGS.SCHOOL_END_TIME,
          value: schoolEndTimeValue
        },
        {
          settings_id: visitorConstants.SETTINGS.SCHOOL_TIMEZONE,
          value: schoolTimeZoneValue
        },
        {
          settings_id: visitorConstants.SETTINGS.AUTOMATIC_CHECKOUT,
          value:
            getGeneralSetting(visitorConstants.SETTINGS.AUTOMATIC_CHECKOUT)
              ?.value || defaultGeneralSettingValues.automaticCheckout
        }
      ]
      await store.dispatch("visitorSettings/updateSchoolGeneralSettings", {
        general_settings: data
      })
      await store.dispatch("visitorSettings/getSchoolGeneralSettings")
    }

    onMounted(async () => {
      try {
        isGeneralSettingsLoading.value = true
        await store.dispatch("visitorSettings/getSchoolGeneralSettings")
      } catch (error) {
        generalSettingsServerError.value = error
      } finally {
        isGeneralSettingsLoading.value = false
      }
    })

    return {
      isLoading,
      activeSettingKey,
      generalSettingsServerError,
      serverError,
      settingList,
      VisitorSchoolHours,
      isGeneralSettingsLoading
    }
  }
}
</script>
